/* Lumi Studio – custom components for Dawn (clean-room implementation) */
(function () {
  'use strict';

  /* ---------- Slider ---------- */
  class LumiSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lumi-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-lumi-prev]');
      this.next = this.querySelector('[data-lumi-next]');
      this.dotsWrap = this.querySelector('[data-lumi-dots]');
      this.loop = this.dataset.loop === 'true';
      this.autoplay = parseFloat(this.dataset.autoplay || '0');
      this.paused = false;

      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => {
        window.clearTimeout(this._t);
        this._t = window.setTimeout(() => this.update(), 80);
      }, { passive: true });
      window.addEventListener('resize', () => this.build());

      ['mouseenter', 'touchstart', 'focusin'].forEach((e) =>
        this.addEventListener(e, () => (this.paused = true), { passive: true })
      );
      ['mouseleave', 'touchend', 'focusout'].forEach((e) =>
        this.addEventListener(e, () => (this.paused = false), { passive: true })
      );

      this.build();
      if (this.autoplay > 0) {
        this.timer = window.setInterval(() => {
          if (!this.paused && this.isVisible()) this.go(1, true);
        }, this.autoplay * 1000);
      }
    }
    disconnectedCallback() {
      window.clearInterval(this.timer);
    }
    isVisible() {
      const r = this.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    }
    perView() {
      const first = this.slides[0];
      if (!first) return 1;
      const w = first.getBoundingClientRect().width || 1;
      return Math.max(1, Math.round(this.track.clientWidth / w));
    }
    pages() {
      return Math.max(1, this.slides.length - this.perView() + 1);
    }
    index() {
      const first = this.slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      return Math.round(this.track.scrollLeft / step);
    }
    build() {
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.classList.toggle('lumi-slider--static', !overflow);
      if (this.dotsWrap) {
        this.dotsWrap.innerHTML = '';
        if (overflow) {
          for (let i = 0; i < this.pages(); i++) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'lumi-slider__dot';
            b.setAttribute('aria-label', String(i + 1));
            b.addEventListener('click', () => this.scrollToIndex(i));
            this.dotsWrap.appendChild(b);
          }
        }
      }
      this.update();
    }
    scrollToIndex(i) {
      const s = this.slides[i];
      if (!s) return;
      this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }
    go(dir, auto) {
      let i = this.index() + dir;
      const last = this.pages() - 1;
      if (i > last) i = this.loop || auto ? 0 : last;
      if (i < 0) i = this.loop ? last : 0;
      this.scrollToIndex(i);
    }
    update() {
      const i = this.index();
      const last = this.pages() - 1;
      if (this.prev) this.prev.disabled = !this.loop && i <= 0;
      if (this.next) this.next.disabled = !this.loop && i >= last;
      if (this.dotsWrap) {
        Array.from(this.dotsWrap.children).forEach((d, n) => d.classList.toggle('is-active', n === Math.min(i, last)));
      }
    }
  }
  if (!customElements.get('lumi-slider')) customElements.define('lumi-slider', LumiSlider);

  /* ---------- Before / after ---------- */
  class LumiBeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const set = () => this.style.setProperty('--lumi-pos', range.value + '%');
      range.addEventListener('input', set);
      set();
    }
  }
  if (!customElements.get('lumi-before-after')) customElements.define('lumi-before-after', LumiBeforeAfter);

  /* ---------- Ticker (marquee) ---------- */
  class LumiTicker extends HTMLElement {
    connectedCallback() {
      this.group = this.querySelector('[data-lumi-ticker-group]');
      if (!this.group) return;
      this.fill();
      window.addEventListener('resize', () => this.fill());
    }
    fill() {
      const inner = this.group.parentElement;
      inner.querySelectorAll('[data-clone]').forEach((c) => c.remove());
      const w = this.group.getBoundingClientRect().width || 1;
      const need = Math.max(1, Math.ceil((this.clientWidth * 2) / w));
      for (let i = 0; i < need; i++) {
        const c = this.group.cloneNode(true);
        c.setAttribute('data-clone', '');
        c.setAttribute('aria-hidden', 'true');
        inner.appendChild(c);
      }
      const speed = parseFloat(this.dataset.speed || '1');
      const seconds = Math.max(4, w / (40 * speed));
      this.style.setProperty('--lumi-ticker-duration', seconds + 's');
      this.style.setProperty('--lumi-ticker-distance', '-' + w + 'px');
    }
  }
  if (!customElements.get('lumi-ticker')) customElements.define('lumi-ticker', LumiTicker);

  /* ---------- Shipping timeline dates ---------- */
  class LumiShippingDates extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.days || '').split(',').map((s) => s.trim());
      const months = (this.dataset.months || '').split(',').map((s) => s.trim());
      const skipDays = (this.dataset.skipDays || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      const fmt = this.dataset.format || 'dd_mm_no_dot';
      // JS getDay(): 0=Sunday. Labels are Monday-first.
      const label = (d) => days[(d.getDay() + 6) % 7] || '';
      const isSkipped = (d) => skipDays.includes(label(d).toLowerCase());
      const addBusiness = (n) => {
        const d = new Date();
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (!isSkipped(d)) added++;
        }
        return d;
      };
      const format = (d) => {
        const day = d.getDate();
        const mon = months[d.getMonth()] || d.getMonth() + 1;
        if (fmt === 'dd_mm') return day + '. ' + mon;
        if (fmt === 'day_dd_mm') return label(d) + ', ' + day + ' ' + mon;
        return day + ' ' + mon;
      };
      this.querySelectorAll('[data-min]').forEach((el) => {
        const min = parseInt(el.dataset.min, 10) || 0;
        const max = parseInt(el.dataset.max, 10) || 0;
        el.innerHTML = el.innerHTML
          .replace(/\[start_date\]/g, format(addBusiness(min)))
          .replace(/\[end_date\]/g, format(addBusiness(max)));
      });
    }
  }
  if (!customElements.get('lumi-shipping-dates')) customElements.define('lumi-shipping-dates', LumiShippingDates);

  /* ---------- Tabs ---------- */
  class LumiTabs extends HTMLElement {
    connectedCallback() {
      const btns = this.querySelectorAll('[role="tab"]');
      const panels = this.querySelectorAll('[role="tabpanel"]');
      btns.forEach((b, i) =>
        b.addEventListener('click', () => {
          btns.forEach((x, n) => x.setAttribute('aria-selected', String(n === i)));
          panels.forEach((p, n) => (p.hidden = n !== i));
        })
      );
    }
  }
  if (!customElements.get('lumi-tabs')) customElements.define('lumi-tabs', LumiTabs);

  /* ---------- Cart reservation timer ---------- */
  class LumiCartTimer extends HTMLElement {
    connectedCallback() {
      const minutes = parseInt(this.dataset.minutes || '10', 10);
      const key = 'lumi-cart-timer';
      let end;
      try {
        end = parseInt(sessionStorage.getItem(key) || '0', 10);
      } catch (e) {}
      if (!end || end < Date.now()) {
        end = Date.now() + minutes * 60000;
        try {
          sessionStorage.setItem(key, String(end));
        } catch (e) {}
      }
      const out = this.querySelector('[data-timer]');
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        const m = Math.floor(left / 60000);
        const s = Math.floor((left % 60000) / 1000);
        if (out) out.textContent = m + ':' + String(s).padStart(2, '0');
        if (left <= 0) window.clearInterval(this.iv);
      };
      tick();
      this.iv = window.setInterval(tick, 1000);
    }
    disconnectedCallback() {
      window.clearInterval(this.iv);
    }
  }
  if (!customElements.get('lumi-cart-timer')) customElements.define('lumi-cart-timer', LumiCartTimer);

  /* ---------- Videos: play only when visible ---------- */
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              const v = e.target;
              if (e.isIntersecting) {
                const p = v.play();
                if (p && p.catch) p.catch(() => {});
              } else v.pause();
            }),
          { threshold: 0.25 }
        )
      : null;
  const watch = (root) => {
    if (!io) return;
    root.querySelectorAll('video[data-lumi-autoplay]').forEach((v) => io.observe(v));
  };
  document.addEventListener('DOMContentLoaded', () => watch(document));
  document.addEventListener('shopify:section:load', (e) => watch(e.target));

  /* Play buttons on videos */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-lumi-play]');
    if (!btn) return;
    const v = btn.parentElement.querySelector('video');
    if (!v) return;
    if (v.muted && !v.paused) {
      v.muted = false;
      v.currentTime = 0;
      btn.classList.add('is-playing');
      return;
    }
    if (v.paused) {
      v.muted = false;
      v.play();
      btn.classList.add('is-playing');
    } else {
      v.pause();
      btn.classList.remove('is-playing');
    }
  });
})();
